/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactElement } from "react";
import { toast, ToastOptions, ToastPosition } from "react-toastify";
import { checkMobile } from "src/schema/validations";

import { USER_TYPE } from "@constants/Bookings";
import { COOKIE_CONSENT_KEY } from "@constants/cookies";
import { CONSTANTS } from "@constants/index";
import { DECIMAL_PLACES } from "@constants/numbers";
import { CONTACT_TYPE } from "@constants/register";
import { LocationPositionType } from "@models/components/organisation.model";
import {
  CustomToastifyProps,
  ProfileCardNameProps,
} from "@models/shared/shared.model";
import appSlice from "@slices/app.slice";
import { store } from "@store/index";

interface FlattenObject {
  [key: string]: string | FlattenObject;
}
const flattenObjectToString = function (ob: FlattenObject | string) {
  if (typeof ob === "string") return ob;
  const toReturn: { [key: string]: string } = {};
  let errorString: string = "";
  for (const i in ob) {
    if (!Object.prototype.hasOwnProperty.call(ob, i)) continue;
    if (typeof ob[i] == "object") {
      toReturn[i] = flattenObjectToString(ob[i] as FlattenObject);
    } else {
      toReturn[i] = ob[i] as string;
    }
  }

  for (const key in toReturn) {
    errorString =
      errorString === ""
        ? errorString + toReturn[key]
        : errorString + ", " + toReturn[key];
  }
  return errorString;
};

const toastService = (
  message: string,
  type: string = CONSTANTS.TOAST_TYPE.SUCCESS,
  autoClose: boolean | number = 5000,
  position: ToastPosition = "bottom-center"
) => {
  const config: ToastOptions = {
    autoClose: autoClose === true ? undefined : autoClose,
    closeOnClick: true,
    draggable: true,
    hideProgressBar: false,
    pauseOnHover: true,
    position: position,
    progress: undefined,
  };

  switch (type) {
    case CONSTANTS.TOAST_TYPE.ERROR:
      toast.error(message, { ...config });
      break;
    case CONSTANTS.TOAST_TYPE.INFO:
      toast.info(message, { ...config });
      break;
    case CONSTANTS.TOAST_TYPE.SUCCESS:
      toast.success(message, { ...config });
      break;
    case CONSTANTS.TOAST_TYPE.WARNING:
      toast.warning(message, { ...config });
      break;
  }
};
const getCardNameByType = (guest: ProfileCardNameProps): string => {
  switch (guest.contactType) {
    case CONTACT_TYPE.SINGLE:
    case USER_TYPE.GUEST:
      return `${guest.firstName ?? ""} ${guest.lastName ?? ""}`.trim();
    case CONTACT_TYPE.COMPANY_WITHOUT_CONTACT:
      return guest.companyName ?? "";
    case CONTACT_TYPE.COMPANY_WITH_CONTACT: {
      const fullName =
        `${guest.firstName ?? ""} ${guest.lastName ?? ""}`.trim();
      return `${guest.companyName ?? ""} (${fullName})`.trim();
    }
    default:
      return "";
  }
};
const toastServiceWithOptions = (
  CustomToastify: ReactElement<CustomToastifyProps>,
  options: ToastOptions = {}
) => {
  const defaultOptions: ToastOptions = {
    autoClose: false,
    closeOnClick: true,
    draggable: false,
    hideProgressBar: true,
    pauseOnHover: true,
    position: "bottom-center",
  };

  toast(CustomToastify, { ...defaultOptions, ...options });
};

export const isMobile = () => {
  return checkMobile.test(navigator.userAgent);
};

const getAttributeFromLocation = (response: any, key: string) => {
  return response[0]?.address_components.find((component: any) =>
    component.types.includes(key)
  );
};

export const fetchCurrentLocationWithGoogleMap = async (
  position: LocationPositionType,
  setLoader: (value: boolean) => void,
  locationSetting?: (city: string, country: string) => void
) => {
  if (!window.google || !window.google.maps) {
    return;
  }
  const { latitude, longitude } = position;
  if (latitude && longitude) {
    const geocoder = new window.google.maps.Geocoder();

    geocoder?.geocode(
      { location: { lat: latitude, lng: longitude } },
      (results: any, status: any) => {
        if (status !== "OK") {
          return console.error("Geocoder failed due to: " + status);
        }

        const [firstResult] = results;
        if (!firstResult) return console.error("No results found.");

        const country = getAttributeFromLocation(
          results,
          CONSTANTS.BROWSER_LOCATION.COUNTRY
        );
        const city = getAttributeFromLocation(
          results,
          CONSTANTS.BROWSER_LOCATION.LOCALITY
        );
        const countryCode = country?.short_name;

        store.dispatch(
          appSlice.actions.findLocation({
            city: city?.long_name,
            country: country?.long_name,
            countryCode,
          })
        );

        if (locationSetting) {
          locationSetting(city?.long_name, countryCode);
        }

        setLoader(false);
      }
    );
  }
};

const commonUtils = {
  fetchCurrentLocationWithGoogleMap,
  flattenObjectToString,
  getCardNameByType,
  isMobile,
  toastService,
  toastServiceWithOptions,
};

export const toCapitalize = (val: string) =>
  String(val)?.charAt(0).toUpperCase() + String(val)?.slice(1);

export const haveCommonItems = (
  array1: Array<string>,
  array2: Array<string>
) => {
  for (let i = 0; i < array1.length; i++) {
    for (let j = 0; j < array2.length; j++) {
      if (array1[i] === array2[j]) {
        return true;
      }
    }
  }
  return false;
};

export const checkPricingDefined = (slotsData: any) => {
  if (slotsData["time-slots"].length === 0) {
    return false;
  } else {
    return true;
  }
};

export const mergeClassNames = (
  ...classNames: (string | undefined | null)[]
): string => {
  return [...new Set(classNames.join(" ").split(" ").filter(Boolean))].join(
    " "
  );
};

export function generateRandomId(prefix = "fg-booking") {
  const randomNumber = Math.floor(Math.random() * 100000);
  return `${prefix}-${randomNumber}`;
}

export function getMinutesDiff(start: string, end: string): number {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return (endDate.getTime() - startDate.getTime()) / (1000 * 60);
}

export const toMinutes = (time: string) => {
  if (!time) return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

export const formatPrice = (
  amount: number,
  currency = "",
  fixDecimalPlaces = false,
  decimalPlaces = DECIMAL_PLACES
): string => {
  if (!currency) return `${amount.toFixed(decimalPlaces)}`;
  if (Number.isNaN(amount)) return `${currency} 0.00`;
  return `${currency} ${amount.toFixed(fixDecimalPlaces ? 0 : decimalPlaces)}`;
};

export const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const setCookie = (name: string, value: string) => {
  let expires = "";
  const date = new Date();
  date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
  expires = `; expires=${date.toUTCString()}`;

  document.cookie = `${name}=${encodeURIComponent(
    value
  )}${expires}; path=/; SameSite=Lax`;
};

export const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export const deleteAllCookies = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  const specialTLDs = ["co.in", "com.de", "co.fr", "co.it"];

  const lastTwo = parts.slice(-2).join(".");
  const lastThree = parts.slice(-3).join(".");

  const rootDomain = specialTLDs.includes(lastTwo) ? lastThree : lastTwo;
  document.cookie.split(";").forEach(cookie => {
    const cookieName = cookie.split("=")[0].trim();

    if (cookieName !== COOKIE_CONSENT_KEY) {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;

      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${rootDomain}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;

      const domainParts = window.location.hostname.split(".");
      if (domainParts.length > 2) {
        const parentDomain = domainParts.slice(-2).join(".");
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${parentDomain}`;
      }
    }
  });
};

export default commonUtils;
